import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { format, startOfWeek, addDays, getISOWeek } from 'date-fns'
import { fr } from 'date-fns/locale'

const MODE_LABELS = { cheque: 'Chèque', especes: 'Espèces' }

export default function RemiseBanque() {
  const { praticien } = useAuth()
  // Lundi de la semaine en cours
  const [lundi, setLundi] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }))
  const [factures, setFactures] = useState([])
  const [loading, setLoading] = useState(false)

  const debut = format(lundi, 'yyyy-MM-dd')
  const fin = format(addDays(lundi, 6), 'yyyy-MM-dd')

  async function load() {
    if (!praticien?.id) return
    setLoading(true)
    try {
      const { data } = await supabase
        .from('factures')
        .select('*')
        .eq('praticien_id', praticien.id)
        .eq('statut', 'payee')
        .in('mode_paiement', ['cheque', 'especes'])
        .gte('date_facture', debut)
        .lte('date_facture', fin)
        .order('date_facture')

      const ids = [...new Set((data || []).map(f => f.patient_id).filter(Boolean))]
      let map = {}
      if (ids.length) {
        const { data: pts } = await supabase.from('patients').select('id, prenom, nom').in('id', ids)
        ;(pts || []).forEach(p => { map[p.id] = p })
      }
      setFactures((data || []).map(f => ({ ...f, patients: map[f.patient_id] || null })))
    } catch (e) {
      console.error(e)
    }
    setLoading(false)
  }

  const loadCallback = useCallback(load, [praticien?.id, debut, fin])
  useEffect(() => { loadCallback() }, [loadCallback])

  async function toggleDepose(f) {
    const newVal = !f.depose_banque
    const dateDepot = newVal ? new Date().toISOString().slice(0, 10) : null
    const { error } = await supabase.from('factures')
      .update({ depose_banque: newVal, date_depot: dateDepot }).eq('id', f.id)
    if (error) { alert('Erreur : ' + error.message); return }
    setFactures(prev => prev.map(x => x.id === f.id ? { ...x, depose_banque: newVal, date_depot: dateDepot } : x))
  }

  async function toutDeposerSemaine() {
    const aDeposer = factures.filter(f => !f.depose_banque)
    if (aDeposer.length === 0) return
    if (!window.confirm(`Marquer ${aDeposer.length} encaissement(s) de la semaine comme déposés en banque ?`)) return
    const dateDepot = new Date().toISOString().slice(0, 10)
    const ids = aDeposer.map(f => f.id)
    const { error } = await supabase.from('factures')
      .update({ depose_banque: true, date_depot: dateDepot }).in('id', ids)
    if (error) { alert('Erreur : ' + error.message); return }
    setFactures(prev => prev.map(x => ids.includes(x.id) ? { ...x, depose_banque: true, date_depot: dateDepot } : x))
  }

  const cheques = factures.filter(f => f.mode_paiement === 'cheque')
  const especes = factures.filter(f => f.mode_paiement === 'especes')

  function recap(liste) {
    const aDep = liste.filter(f => !f.depose_banque)
    const dep = liste.filter(f => f.depose_banque)
    return {
      aDeposerTotal: aDep.reduce((s, f) => s + f.montant, 0), aDeposerNb: aDep.length,
      deposeTotal: dep.reduce((s, f) => s + f.montant, 0), deposeNb: dep.length,
    }
  }
  const rc = recap(cheques)
  const re = recap(especes)
  const resteADeposer = rc.aDeposerNb + re.aDeposerNb

  function Bloc({ titre, emoji, liste, r }) {
    return (
      <div style={{flex:1, minWidth:300, background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:16}}>
        <div style={{display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:10}}>
          <h3 style={{margin:0, fontSize:15}}>{emoji} {titre}</h3>
          <div style={{fontSize:13}}>
            <span style={{color:'#C62828', fontWeight:600}}>À déposer : {r.aDeposerTotal.toFixed(0)} € ({r.aDeposerNb})</span>
            <span style={{color:'var(--text-2)', marginLeft:10}}>Déposé : {r.deposeTotal.toFixed(0)} € ({r.deposeNb})</span>
          </div>
        </div>
        {liste.length === 0 ? (
          <div style={{fontSize:13, color:'var(--text-3)', padding:'8px 0'}}>Aucun {titre.toLowerCase()} cette semaine.</div>
        ) : (
          <div style={{display:'flex', flexDirection:'column', gap:4}}>
            {liste.map(f => (
              <label key={f.id} style={{display:'flex', alignItems:'center', gap:10, padding:'7px 10px', borderRadius:6, cursor:'pointer',
                background: f.depose_banque ? '#F1F8E9' : '#FFF5F5', border:'1px solid ' + (f.depose_banque ? '#C5E1A5' : '#FFCDD2')}}>
                <input type="checkbox" checked={!!f.depose_banque} onChange={() => toggleDepose(f)} />
                <span style={{flex:1, fontSize:13}}>
                  {f.patients ? `${f.patients.prenom} ${f.patients.nom}` : '—'}
                  <span style={{color:'var(--text-2)', marginLeft:8, fontSize:12}}>{format(new Date(f.date_facture), 'EEEE dd/MM', {locale:fr})}</span>
                </span>
                <span style={{fontWeight:600, fontSize:13}}>{f.montant} €</span>
                <span style={{fontSize:11, width:90, textAlign:'right', color: f.depose_banque ? '#2E7D32' : '#C62828'}}>
                  {f.depose_banque ? (f.date_depot ? 'déposé ' + format(new Date(f.date_depot), 'dd/MM') : 'déposé') : 'à déposer'}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:16, flexWrap:'wrap'}}>
        <button className="btn btn-sm" onClick={() => setLundi(l => addDays(l, -7))}>◀</button>
        <div style={{textAlign:'center', minWidth:240}}>
          <div style={{fontSize:15, fontWeight:600}}>Semaine {getISOWeek(lundi)}</div>
          <div style={{fontSize:12, color:'var(--text-2)'}}>
            du {format(lundi, 'dd/MM/yyyy')} au {format(addDays(lundi, 6), 'dd/MM/yyyy')}
          </div>
        </div>
        <button className="btn btn-sm" onClick={() => setLundi(l => addDays(l, 7))}>▶</button>
        <button className="btn btn-sm" onClick={() => setLundi(startOfWeek(new Date(), { weekStartsOn: 1 }))}>Cette semaine</button>
        <div style={{flex:1}} />
        {resteADeposer > 0 && (
          <button className="btn btn-sm" style={{background:'#185FA5', color:'#fff', border:'none', fontWeight:600}}
            onClick={toutDeposerSemaine}>
            ✓ Tout marquer déposé ({resteADeposer})
          </button>
        )}
      </div>

      <div style={{display:'flex', alignItems:'center', gap:16, padding:'12px 16px', background:'#F4F7FB', border:'1px solid #D6E2F0', borderRadius:'var(--radius)', marginBottom:16, flexWrap:'wrap'}}>
        <span style={{fontSize:14, fontWeight:600, color:'#0C447C'}}>À déposer cette semaine :</span>
        <span style={{fontSize:14}}>🧾 Chèques <strong>{rc.aDeposerTotal.toFixed(0)} €</strong> ({rc.aDeposerNb})</span>
        <span style={{fontSize:14}}>💶 Espèces <strong>{re.aDeposerTotal.toFixed(0)} €</strong> ({re.aDeposerNb})</span>
        <span style={{fontSize:14, fontWeight:600, color:'#0C447C'}}>= Total {(rc.aDeposerTotal + re.aDeposerTotal).toFixed(0)} €</span>
      </div>

      {loading ? <div className="empty">Chargement…</div> : (
        <div style={{display:'flex', gap:16, flexWrap:'wrap'}}>
          <Bloc titre="Chèques" emoji="🧾" liste={cheques} r={rc} />
          <Bloc titre="Espèces" emoji="💶" liste={especes} r={re} />
        </div>
      )}

      <p style={{fontSize:12, color:'var(--text-3)', marginTop:16}}>
        Astuce : un chèque que tu déposes plus tard (patient qui demande de décaler) reste « à déposer » dans sa semaine — reviens-y avec ◀ pour le cocher le jour où tu le portes.
      </p>
    </>
  )
}
